import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import type { CoreError } from '@ingenico/api-sdk-core/common';
import { useBusinessService } from '@ingenico/react-section-utils';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { useTranslation } from 'react-i18next';
import { SyncConfigurationStoreModal } from '../components/sync-configuration-store-modal/SyncConfigurationStoreModal';

import { enumDeploymentRequestTriggerTargetTypeV2 } from '@ingenico/api-sdk-device-management-public-v2';
import { Level, showNotification } from '@ingenico/react-notifications';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { toString } from 'lodash-es';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

export interface UseSyncStoreConfigurationModalReturn {
  readonly modalComponent: ReactNode;
  readonly handleOpenModal: (storeId: string) => void;
}

export function useSyncStoreConfigurationModal(): UseSyncStoreConfigurationModalReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.STORE]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);
  const [syncRequestState, setSyncRequestState] = useAsyncDataState<unknown, CoreError>();
  const [storeId, setStoreId] = useState<string>('');

  const onCancel = useCallback(() => {
    setIsModalOpen(false);
    setSyncRequestState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: undefined,
    });
    setStoreId('');
  }, [setSyncRequestState]);
  const onSync = useCallback((): void => {
    requestAndUpdateState(
      deviceManagementBusinessService.triggerTargetDeploymentRequests({
        targets: [
          {
            type: enumDeploymentRequestTriggerTargetTypeV2.STORE,
            id: storeId,
          },
        ],
      }),
      setSyncRequestState,
      {
        onSuccess: () => {
          showNotification({
            content: toString(t('success.syncConfiguration')),
            level: Level.Success,
          });
          onCancel();
        },
        onError: () => {
          showNotification({
            content: toString(t('error.default.syncConfiguration')),
            level: Level.Error,
          });
        },
      },
    );
  }, [storeId, onCancel, deviceManagementBusinessService, setSyncRequestState, t]);

  const handleOpenModal = useCallback(
    (storeIdParam: string) => {
      setStoreId(storeIdParam);
      setIsModalOpen(true);
    },
    [setIsModalOpen, setStoreId],
  );

  return {
    modalComponent: (
      <SyncConfigurationStoreModal
        isOpen={isModalOpen}
        onSync={onSync}
        isLoading={syncRequestState.isLoading}
        onCancel={onCancel}
        storeId={storeId}
      />
    ),
    handleOpenModal,
  };
}
